import PhoneIphoneOutlined from '@mui/icons-material/PhoneIphoneOutlined';
import PhonelinkSetupOutlined from '@mui/icons-material/PhonelinkSetupOutlined';
import WatchLaterOutlined from '@mui/icons-material/WatchLaterOutlined';
import { Box, Chip, Stack, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { NfcCard, Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { orderBy, upperFirst } from 'lodash';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  getCardsByVisitorId,
  getStatusPriority as getStatusPriorityNfcCard,
  isCardBlockedStatus,
  isCardExpiredStatus,
  isCardExpiresSoonStatus,
  isRightsChangedStatus,
  isUserExpiresSoonStatus
} from 'utils/NfcCard/nfcCard';
import {
  getStatusPriority,
  getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers,
  getUserDeletionDate,
  getVisibleVisitorGroupMemberships,
  getVisitorMembershipByVisitorGroupId,
  getVisitorStatuses,
  Statuses,
  translateRole
} from 'utils/Visitor/visitor';
import CardChip, { CardChipColor } from 'v2/components/CardChip/CardChip';
import { CardColumnHeader } from 'v2/components/CardColumnHeader/CardColumnHeader';
import ChipListCell from 'v2/components/ChipListCell/ChipListCell';
import { ColumnHeaderWithTooltip } from 'v2/components/ColumnHeaderWithTooltip/ColumnHeaderWithTooltip';
import { dataGridIdSlots } from 'v2/components/DataGrid/dataGridIdSlots';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import { ExpirationColumnHeader } from 'v2/components/ExpirationCell/ExpirationColumnHeader';
import GroupAvatarCell from 'v2/components/GroupAvatarCell/GroupAvatarCell';
import { OriginCell } from 'v2/components/OriginCell/OriginCell';
import { OriginColumnHeader } from 'v2/components/OriginColumnHeader/OriginColumnHeader';
import RepeatOverflowCounter from 'v2/components/RepeatOverflowCounter/RepeatOverflowCounter';
import { RoleColumnHeader } from 'v2/components/RoleColumnHeader/RoleColumnHeader';
import StatusChip from 'v2/components/StatusChip/StatusChip';
import { StatusColumnHeader } from 'v2/components/StatusColumnHeader/StatusColumnHeader';
import { TwoLineCell } from 'v2/components/TextCell/TextCell';
import { WarningCell } from 'v2/components/Warning/Warning';
import {
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
  DATAGRID_ROW_HEIGHT,
  EMPTY_VALUE_PLACEHOLDER
} from 'v2/constants';
import { getUserPath } from 'v2/paths';
import { formatDate } from 'v2/utils/dateTime';
import { getVisitorOriginLabel } from 'v2/utils/visitorOrigin';

type Variant = 'Page' | 'Tab';

interface Props {
  visitors: Visitor[];
  cards: NfcCard[];
  variant?: Variant;
  loading: boolean;
  className?: string;
  groupId?: string;
}

const getGroupExpirationDate = (visitor: Visitor, groupId: string) =>
  getVisitorMembershipByVisitorGroupId(visitor, groupId)?.expirationDate ?? null;

const getGroupsCell = (visitor: Visitor, translation: Translation) => {
  const groups = getVisibleVisitorGroupMemberships(visitor).map((m) => ({
    id: m.visitorGroup.id,
    name: m.visitorGroup.name
  }));
  if (groups.length === 0) return <WarningCell label={translation.no_groups_assigned} />;
  return <GroupAvatarCell groups={groups} />;
};

const getRolesItems = (visitor: Visitor, translation: Translation) =>
  getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers(visitor).map((role) => ({
    id: role,
    name: upperFirst(translateRole(role, translation))
  }));

const getCardChipColor = (card: NfcCard): CardChipColor => {
  if (isCardBlockedStatus(card)) return 'secondary';
  if (isCardExpiredStatus(card) || isRightsChangedStatus(card)) return 'error';
  if (isCardExpiresSoonStatus(card) || isUserExpiresSoonStatus(card)) return 'warning';
  return 'success';
};

const getCardsCell = (visitor: Visitor, cards: NfcCard[]) => {
  const cardsAssignedToVisitor = getCardsByVisitorId(visitor.id, cards);
  const cardsAssignedToVisitorWithStatusPriority = cardsAssignedToVisitor.map((card) => ({
    ...card,
    statusPriority: getStatusPriorityNfcCard(card)
  }));
  const sortedCardsAssignedToVisitorByStatusPriority = orderBy(
    cardsAssignedToVisitorWithStatusPriority,
    'statusPriority'
  );

  return (
    <Box height="52px" display="flex" alignItems="center">
      <RepeatOverflowCounter
        variant="chip"
        renderMore={(count) => (
          <Chip
            label={<Typography variant="caption">{`+${count}`}</Typography>}
            variant="filled"
            size="small"
            color="secondary"
          />
        )}
      >
        {sortedCardsAssignedToVisitorByStatusPriority.map((card) => (
          <CardChip key={card.uuid} label={card.name} color={getCardChipColor(card)} />
        ))}
      </RepeatOverflowCounter>
    </Box>
  );
};

const getVisitorStatusChips = (
  visitor: Visitor,
  cards: NfcCard[],
  theme: Theme,
  translation: Translation,
  iconOnly: boolean
) => {
  const statuses = getVisitorStatuses(visitor, cards);
  switch (statuses) {
    case Statuses.isConfigDeviceDisconnected:
      return (
        <StatusChip
          color="error"
          icon={<PhonelinkSetupOutlined sx={{ fill: theme.palette.error.main }} />}
          label={translation.disconnected}
          tooltip={translation.access_disconnected_tooltip_config}
          iconOnly={iconOnly}
        />
      );
    case Statuses.isAccessDeviceDisconnectedAndVisitorHasNoCards:
      return (
        <StatusChip
          color="error"
          icon={<PhoneIphoneOutlined sx={{ fill: theme.palette.error.main }} />}
          label={translation.disconnected}
          tooltip={translation.access_disconnected_tooltip_no_card}
          iconOnly={iconOnly}
        />
      );
    case Statuses.isAccessDeviceDisconnectedAndVisitorHasCards:
      return (
        <StatusChip
          color="warning"
          icon={<PhoneIphoneOutlined sx={{ fill: theme.palette.warning.main }} />}
          label={translation.disconnected}
          tooltip={translation.access_disconnected_tooltip_has_card}
          iconOnly={iconOnly}
        />
      );
    case Statuses.isVisitorExpireIn24H:
      return (
        <StatusChip
          color="error"
          icon={<WatchLaterOutlined sx={{ fill: theme.palette.error.main }} />}
          label={upperFirst(translation.expires_in_24h)}
          iconOnly={iconOnly}
        />
      );
    case Statuses.isVisitorExpireIn1Week:
      return (
        <StatusChip
          color="warning"
          icon={<WatchLaterOutlined sx={{ fill: theme.palette.warning.main }} />}
          label={upperFirst(translation.expires_this_week)}
          iconOnly={iconOnly}
        />
      );
    default:
      return (
        <StatusChip
          color="success"
          icon={<PhoneIphoneOutlined sx={{ fill: theme.palette.success.main }} />}
          label={translation.connected}
          iconOnly={iconOnly}
        />
      );
  }
};

const AllUsersOverview: React.FC<Props> = ({ visitors, cards, variant = 'Page', loading, className, groupId }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const isMediumAndAbove = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeAndAbove = useMediaQuery(theme.breakpoints.up('lg'));
  const isExtraLargeAndAbove = useMediaQuery(theme.breakpoints.up('xl'));

  const headerWithId = renderHeaderWithId();

  const rows = useMemo(
    () =>
      visitors.map((visitor) => ({
        id: visitor.id,
        visitor: visitor
      })),
    [visitors]
  );

  const columns: GridColDef<(typeof rows)[number]>[] = useMemo(
    () => [
      {
        field: 'user',
        headerName: translation.user,
        sortable: true,
        flex: 3,
        minWidth: 120,
        renderHeader: headerWithId,
        valueGetter: (_, row) => row.visitor.name,
        renderCell: (params) => {
          const { visitor } = params.row;
          return <TwoLineCell title={visitor.name} content={visitor.email} />;
        }
      },
      {
        field: 'origin',
        headerName: translation.origin,
        sortable: true,
        width: 110,
        valueGetter: (_, row) => getVisitorOriginLabel(row.visitor, translation),
        renderHeader: renderHeaderWithId(undefined, () => <OriginColumnHeader />),
        renderCell: (params) => {
          const { visitor } = params.row;
          return <OriginCell label={getVisitorOriginLabel(visitor, translation)} />;
        }
      },
      {
        field: 'groups',
        headerName: translation.user_groups,
        width: 170,
        sortable: false,
        renderHeader: renderHeaderWithId(undefined, () => (
          <ColumnHeaderWithTooltip
            label={translation.user_groups}
            tooltip={
              <Stack spacing={1}>
                <Typography variant="caption">{translation.user_groups_tooltip_multiple_membership}</Typography>
                <Typography variant="caption">{translation.user_groups_tooltip_admin_installer_roles}</Typography>
              </Stack>
            }
          />
        )),
        renderCell: (params) => {
          const { visitor } = params.row;
          return getGroupsCell(visitor, translation);
        }
      },
      {
        field: 'role',
        headerName: translation.role_or_roles,
        sortable: false,
        minWidth: 150,
        flex: 1,
        renderHeader: renderHeaderWithId(undefined, () => <RoleColumnHeader />),
        renderCell: (params) => {
          const { visitor } = params.row;
          return <ChipListCell items={getRolesItems(visitor, translation)} />;
        }
      },
      {
        field: 'expiration',
        headerName: translation.expiration,
        sortable: true,
        valueGetter: (_, row) =>
          groupId ? getGroupExpirationDate(row.visitor, groupId) : getUserDeletionDate(row.visitor),
        renderHeader: renderHeaderWithId(undefined, () => <ExpirationColumnHeader showTooltip={!groupId} />),
        minWidth: 160,
        renderCell: (params) => {
          const expirationDate = groupId
            ? getGroupExpirationDate(params.row.visitor, groupId)
            : getUserDeletionDate(params.row.visitor);
          if (!expirationDate) return EMPTY_VALUE_PLACEHOLDER;
          return (
            <TwoLineCell
              title={formatDate(expirationDate, 'dateOnly')}
              content={formatDate(expirationDate, 'timeOnly')}
            />
          );
        }
      },
      {
        field: 'card',
        headerName: translation.card,
        sortable: false,
        flex: 1,
        minWidth: 80,
        renderHeader: renderHeaderWithId(undefined, () => <CardColumnHeader />),
        renderCell: (params) => {
          const { visitor } = params.row;
          return getCardsCell(visitor, cards);
        }
      },
      {
        field: 'status',
        headerName: translation.status,
        sortable: true,
        valueGetter: (_, row) => getStatusPriority(row.visitor, cards),
        width: isExtraLargeAndAbove ? 160 : 120,
        renderHeader: renderHeaderWithId(undefined, () => <StatusColumnHeader />),
        renderCell: (params) => {
          const { visitor } = params.row;
          return getVisitorStatusChips(visitor, cards, theme, translation, !isExtraLargeAndAbove);
        }
      }
    ],
    [rows, groupId, cards, theme, translation, headerWithId, isExtraLargeAndAbove]
  );

  const columnVisibilityModel =
    variant === 'Page'
      ? {
          user: true,
          origin: isLargeAndAbove,
          groups: isLargeAndAbove,
          role: isLargeAndAbove,
          expiration: isMediumAndAbove,
          card: isExtraLargeAndAbove,
          status: true
        }
      : {
          user: true,
          origin: false,
          groups: false,
          role: isMediumAndAbove,
          expiration: isMediumAndAbove,
          card: isMediumAndAbove,
          status: true
        };

  return (
    <DataGrid
      className={className}
      rows={rows}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      slots={dataGridIdSlots}
      autoHeight
      hideFooter={rows.length <= DATAGRID_DEFAULT_PAGE_SIZE}
      pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
      rowHeight={DATAGRID_ROW_HEIGHT}
      rowSelection={false}
      onRowClick={({ id }) => navigate(getUserPath(String(id)))}
      loading={loading}
      initialState={{
        sorting: { sortModel: [{ field: 'user', sort: 'asc' }] },
        pagination: { paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE } }
      }}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton'
        }
      }}
      disableRowSelectionOnClick
      disableColumnSelector
      disableRowSelectionExcludeModel
      disableColumnFilter
      disableColumnMenu
    />
  );
};

export default AllUsersOverview;
